import React, { useState, useContext } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { 
  AppBar, 
  Box, 
  Toolbar, 
  IconButton, 
  Typography, 
  Menu, 
  MenuItem, 
  Tooltip, 
  Avatar, 
  Drawer, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText, 
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import BusinessIcon from '@mui/icons-material/Business';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LogoutIcon from '@mui/icons-material/Logout';
import PeopleIcon from '@mui/icons-material/People';
import AssessmentIcon from '@mui/icons-material/Assessment';
import VpnKeyIcon from '@mui/icons-material/VpnKey';

// Kontext
import { AuthContext } from '../../context/AuthContext';
import { APP_VERSION } from '../../version';

// Prüfen, ob der Benutzer die Berechtigung hat, einen Navigationspunkt zu sehen
const hasPermission = (userRole: string | undefined, allowedRoles: string[]): boolean => {
  if (!userRole) return false;
  return allowedRoles.includes(userRole);
};

const drawerWidth = 240;

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useContext(AuthContext);
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Benutzermenü-Handler
  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  // Logout-Handler
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Mobile-Drawer-Handler
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Kombinierte Navigation und Drawer-Schließen Funktion
  const handleNavigation = (path: string) => {
    navigate(path);
    setMobileOpen(false);  // Schließt die Navbar auf mobilen Geräten
  };

  // Hebt den aktuellen Menüpunkt hervor – vorher war nicht erkennbar,
  // auf welcher Seite man sich befindet.
  const istAktiv = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  // Drawer-Inhalt
  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          Zeiterfassung
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {/* Standardfunktionen für alle Benutzer */}
        <ListItem key="Dashboard" disablePadding>
          <ListItemButton onClick={() => handleNavigation('/')} selected={istAktiv('/')}>
            <ListItemIcon><DashboardIcon /></ListItemIcon>
            <ListItemText primary="Dashboard" />
          </ListItemButton>
        </ListItem>
        <ListItem key="Zeiterfassung" disablePadding>
          <ListItemButton
            onClick={() => handleNavigation('/time-entries')}
            selected={istAktiv('/time-entries')}
          >
            <ListItemIcon><AccessTimeIcon /></ListItemIcon>
            <ListItemText primary="Zeiterfassung" />
          </ListItemButton>
        </ListItem>
      </List>
      
      {/* Verwaltungsfunktionen nur für Admin und Projektleiter */}
      {(user?.role === 'Admin' || user?.role === 'Projektleiter') && (
        <>
          <Divider />
          <List subheader={
            <Typography sx={{ fontWeight: 'bold', pl: 2, pt: 1, pb: 0.5 }}>
              Verwaltung
            </Typography>
          }>
            {/* Projekte (nur für Admin und Projektleiter) */}
            {hasPermission(user?.role, ['Admin', 'Projektleiter']) && (
              <ListItem key="Projekte" disablePadding>
                <ListItemButton
                  onClick={() => handleNavigation('/projects')}
                  selected={istAktiv('/projects')}
                >
                  <ListItemIcon><BusinessIcon /></ListItemIcon>
                  <ListItemText primary="Projekte" />
                </ListItemButton>
              </ListItem>
            )}
            
            {/* Berichte (nur für Admin und Projektleiter) */}
            {hasPermission(user?.role, ['Admin', 'Projektleiter']) && (
              <ListItem key="Berichte" disablePadding>
                <ListItemButton
                  onClick={() => handleNavigation('/reports')}
                  selected={istAktiv('/reports')}
                >
                  <ListItemIcon><AssessmentIcon /></ListItemIcon>
                  <ListItemText primary="Berichte" />
                </ListItemButton>
              </ListItem>
            )}
            
            {/* Benutzerverwaltung (nur für Admin) */}
            {hasPermission(user?.role, ['Admin']) && (
              <ListItem key="Benutzerverwaltung" disablePadding>
                <ListItemButton
                  onClick={() => handleNavigation('/users')}
                  selected={istAktiv('/users')}
                >
                  <ListItemIcon><PeopleIcon /></ListItemIcon>
                  <ListItemText primary="Benutzerverwaltung" />
                </ListItemButton>
              </ListItem>
            )}
          </List>
        </>
      )}

      {/* Angemeldeter Benutzer und Version am unteren Rand */}
      <Box sx={{ mt: 'auto', p: 2 }}>
        <Divider sx={{ mb: 1.5 }} />
        {user && (
          <>
            <Typography variant="body2" noWrap title={user.name}>
              {user.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {user.role}
            </Typography>
          </>
        )}
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          Version {APP_VERSION}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="Navigationsmenü öffnen"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          {/* Logo */}
          <Box
            component="img"
            sx={{
              height: { xs: 25, sm: 40 },  // Noch kleinere Höhe auf mobilen Geräten
              mr: { xs: 1, sm: 2 },        // Kleinerer Abstand auf mobilen Geräten
              display: 'block'
            }}
            alt="Logo"
            src="/logo.jpg"
          />
          
          {/* Flexibler Platz */}
          <Box sx={{ flexGrow: 1 }} />

          {/* Benutzerprofil und Menü */}
          <Box sx={{ flexGrow: 0 }}>
            <Tooltip title="Einstellungen öffnen">
              <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                <Avatar alt={user?.name || 'Benutzer'}>
                  {user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
                </Avatar>
              </IconButton>
            </Tooltip>
            <Menu
              sx={{ mt: '45px' }}
              id="menu-appbar"
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              <MenuItem onClick={() => {
                handleCloseUserMenu();
                handleNavigation('/change-password');
              }}>
                <VpnKeyIcon sx={{ mr: 1 }} />
                <Typography textAlign="center">Passwort ändern</Typography>
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <LogoutIcon sx={{ mr: 1 }} />
                <Typography textAlign="center">Abmelden</Typography>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Navigation Drawer */}
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        {/* Mobile-Version */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Bessere Mobile-Performance
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop-Version */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Hauptinhalt */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: isMobile ? 1.5 : 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: '64px', // AppBar-Höhe
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default Layout; 